import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from '@mui/material'
import { WorkingStepIconSvg } from 'components/Common/WorkingSteps/WorkingStepIcon'
import { useLocalizedWorkingStepLabel } from 'components/Localization/useLocalizedWorkingStepLabel'
import { MaterialsController } from 'controllers/MaterialsController'
import { RowThumbnailButton } from 'features/BillOfMaterials/components/BomItemThumbnailButton'
import { fetchProject } from 'features/BillOfMaterials/store/asyncActions/fetchProject'
import { bomItemActivitiesSelectors } from 'features/BillOfMaterials/store/selectors/activities/bomItemActivitiesSelectors'
import { bomItemSelector } from 'features/BillOfMaterials/store/selectors/bomItemSelector'
import { isEqual } from 'lodash'
import { BomItemType, PartTypeRow } from 'model/Project/BoMItemRow'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  BoMItemActivityDto,
  KeywordDto,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { ShowException } from 'store/Application/appActions'
import {
  RootAppState,
  useAppDispatch,
  useAppSelector,
} from 'store/configureStore'

const EXCLUDED_KEYWORD_CATEGORIES = ['shape', 'dimensions']

/** Filter out shape/dimensions keywords from a keyword map */
const filterCopyableKeywords = (
  keywords: Record<string, KeywordDto[]> | undefined,
): Record<string, KeywordDto[]> => {
  if (!keywords) return {}
  return Object.fromEntries(
    Object.entries(keywords).filter(
      ([category]) => !EXCLUDED_KEYWORD_CATEGORIES.includes(category),
    ),
  )
}

/** Check if a keyword map has any copyable (non-shape/dimensions) keywords */
const hasCopyableKeywords = (
  keywords: Record<string, KeywordDto[]> | undefined,
): boolean => {
  if (!keywords) return false
  return Object.entries(keywords).some(
    ([category, kws]) =>
      !EXCLUDED_KEYWORD_CATEGORIES.includes(category) && kws?.length > 0,
  )
}

export type CopyKeywordsDialogProps = {
  selectedPointers: BomItemPointer[]
  onClose?: () => void
}

type Props = CopyKeywordsDialogProps & {
  open: boolean
  onClose: () => void
}

export const CopyKeywordsDialog = ({
  selectedPointers,
  open,
  onClose,
}: Props) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const getLocalizedWorkingStep = useLocalizedWorkingStepLabel()

  const [sourcePointerId, setSourcePointerId] = useState<string>('')
  const [selectedActivityIds, setSelectedActivityIds] = useState<string[]>([])
  const [saving, setSaving] = useState(false)

  const projectId = useAppSelector(
    (state: RootAppState) => state.project.activeProject?.id,
  )

  // get bom item data for all selected pointers
  const bomItems = useAppSelector(
    (state: RootAppState) =>
      selectedPointers
        .map((p) => bomItemSelector(state, p))
        .filter(
          (item) =>
            item &&
            (item.type === BomItemType.partType ||
              item.type === BomItemType.partInstance),
        ) as PartTypeRow[],
  )

  // get hydrated activities (with proper keywords from rawMaterialSummary)
  const activitiesByItemId = useAppSelector((state: RootAppState) => {
    const map: Record<string, BoMItemActivityDto[]> = {}
    selectedPointers.forEach((p) => {
      const activities = bomItemActivitiesSelectors.allActivities(p)(state)
      if (activities) {
        map[p.id] = activities.filter(Boolean)
      }
    })
    return map
  }, isEqual)

  const sourcePointer = useMemo(
    () => selectedPointers.find((p) => p.id === sourcePointerId),
    [selectedPointers, sourcePointerId],
  )

  // items that have activities with copyable keywords
  const sourceEligibleItems = useMemo(
    () =>
      bomItems.filter((item) => {
        const activities = activitiesByItemId[item.id]
        return activities?.some((a) => hasCopyableKeywords(a?.keywords))
      }),
    [bomItems, activitiesByItemId],
  )

  // default to first eligible item
  useEffect(() => {
    if (!sourcePointerId && sourceEligibleItems.length > 0) {
      setSourcePointerId(sourceEligibleItems[0].id)
    }
  }, [sourcePointerId, sourceEligibleItems])

  const sourceItem = useMemo(
    () => bomItems.find((item) => item.id === sourcePointerId),
    [bomItems, sourcePointerId],
  )

  // activities from source that have copyable keywords
  const availableActivities = useMemo(() => {
    const activities = activitiesByItemId[sourcePointerId]
    if (!activities) return []
    return activities.filter((a) => hasCopyableKeywords(a?.keywords))
  }, [activitiesByItemId, sourcePointerId])

  // only show destination items that have at least one matching working step
  // from the selected source activities
  const selectedWorkingSteps = useMemo(() => {
    return availableActivities
      .filter((a) => selectedActivityIds.includes(a.id))
      .map((a) => a.primaryWorkingStep)
  }, [availableActivities, selectedActivityIds])

  const destinationItems = useMemo(
    () =>
      bomItems.filter(
        (item) =>
          item.id !== sourcePointerId &&
          activitiesByItemId[item.id]?.some((a) =>
            selectedWorkingSteps.includes(a?.primaryWorkingStep),
          ),
      ),
    [bomItems, sourcePointerId, selectedWorkingSteps, activitiesByItemId],
  )

  const handleToggleActivity = useCallback((activityId: string) => {
    setSelectedActivityIds((prev) =>
      prev.includes(activityId)
        ? prev.filter((id) => id !== activityId)
        : [...prev, activityId],
    )
  }, [])

  const handleSourceChange = useCallback((newSourceId: string) => {
    setSourcePointerId(newSourceId)
    setSelectedActivityIds([])
  }, [])

  const handleSave = useCallback(async () => {
    if (
      !sourceItem ||
      selectedActivityIds.length === 0 ||
      destinationItems.length === 0
    ) {
      return
    }

    setSaving(true)
    try {
      const selectedActivities = availableActivities.filter((a) =>
        selectedActivityIds.includes(a.id),
      )

      // Collect all copyable keywords from selected source activities
      const allKeywords: KeywordDto[] = []
      for (const sourceActivity of selectedActivities) {
        const copyable = filterCopyableKeywords(sourceActivity.keywords)
        allKeywords.push(...Object.values(copyable).flat())
      }

      // Get destination item pointers
      const destPointers = destinationItems
        .map((item) => selectedPointers.find((p) => p.id === item.id))
        .filter(Boolean)

      if (allKeywords.length > 0 && destPointers.length > 0) {
        const materialsController = new MaterialsController()
        await materialsController.AddKeywordsToRows({
          keywords: allKeywords,
          bomItemPointers: destPointers,
        })
      }

      dispatch(fetchProject({ projectId }))
      onClose()
    } catch (err) {
      ShowException('copy keywords', err)
    } finally {
      setSaving(false)
    }
  }, [
    sourceItem,
    selectedActivityIds,
    destinationItems,
    availableActivities,
    selectedPointers,
    dispatch,
    projectId,
    onClose,
  ])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {t('project:copy-keywords-title', 'Copy keywords to selected items')}
      </DialogTitle>
      <DialogContent sx={{ height: '60vh', overflow: 'hidden', p: 0 }}>
        <Box sx={{ display: 'flex', gap: 2, height: '100%', p: 2 }}>
          {/* Left column: Source item + activities with keywords */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              {t('project:copy-keywords-source', 'Source item')}
            </Typography>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <Select
                value={sourcePointerId}
                onChange={(e) => handleSourceChange(e.target.value as string)}
                renderValue={(selectedId) => {
                  const item = sourceEligibleItems.find(
                    (i) => i.id === selectedId,
                  )
                  const pointer = selectedPointers.find(
                    (p) => p.id === selectedId,
                  )
                  if (!item || !pointer) return selectedId
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption">
                        {item.rowNumber}.
                      </Typography>
                      <RowThumbnailButton
                        bomItemPointer={pointer}
                        size="small"
                        disableClick
                      />
                      <Typography variant="body1" fontWeight="bold">
                        {item.name || item.id}
                      </Typography>
                    </Box>
                  )
                }}
              >
                {sourceEligibleItems.map((item) => {
                  const pointer = selectedPointers.find((p) => p.id === item.id)
                  return (
                    <MenuItem key={item.id} value={item.id}>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <Typography variant="caption">
                          {item.rowNumber}.
                        </Typography>
                        <RowThumbnailButton
                          bomItemPointer={pointer}
                          size="small"
                          disableClick
                        />
                        <Typography variant="body1" fontWeight="bold">
                          {item.name || item.id}
                        </Typography>
                      </Box>
                    </MenuItem>
                  )
                })}
              </Select>
            </FormControl>

            <Typography variant="subtitle2" gutterBottom>
              {t(
                'project:copy-keywords-select-activities',
                'Activities to copy keywords from',
              )}
            </Typography>

            {availableActivities.length === 0 ? (
              <Box sx={{ py: 2, textAlign: 'center', color: 'text.secondary' }}>
                {t(
                  'project:copy-keywords-no-activities',
                  'No activities with keywords on this item',
                )}
              </Box>
            ) : (
              <List dense sx={{ flex: 1, overflow: 'auto' }}>
                {availableActivities.map((activity: BoMItemActivityDto) => {
                  const checked = selectedActivityIds.includes(activity.id)
                  return (
                    <ListItem key={activity.id} disablePadding>
                      <ListItemButton
                        onClick={() => handleToggleActivity(activity.id)}
                        sx={{
                          gap: 2,
                        }}
                      >
                        <Checkbox
                          edge="start"
                          checked={checked}
                          disableRipple
                        />
                        <WorkingStepIconSvg
                          workingStepType={activity.primaryWorkingStep}
                          disableTooltip
                          attributes={{
                            width: 32,
                            height: 32,
                          }}
                        />
                        <ListItemText
                          primary={getLocalizedWorkingStep(
                            activity.primaryWorkingStep,
                          )}
                          secondary={Object.values(
                            filterCopyableKeywords(activity.keywords),
                          )
                            .flat()
                            .map((k) => k.originalKeyword)
                            .join(' | ')}
                        />
                      </ListItemButton>
                    </ListItem>
                  )
                })}
              </List>
            )}
          </Box>

          <Divider orientation="vertical" flexItem />

          {/* Right column: Destination items */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              {t(
                'project:copy-keywords-destinations',
                'Destination items ({{count}})',
                { count: destinationItems.length },
              )}
            </Typography>
            <List dense sx={{ flex: 1, overflow: 'auto' }}>
              {destinationItems.map((item) => {
                const pointer = selectedPointers.find((p) => p.id === item.id)
                const destActivities = activitiesByItemId[item.id] || []
                // merge all keywords from all activities (show all for destination context)
                const allKeywords: Record<string, KeywordDto[]> = {}
                destActivities.filter(Boolean).forEach((a) => {
                  Object.entries(a.keywords || {}).forEach(([cat, kws]) => {
                    if (!allKeywords[cat]) allKeywords[cat] = []
                    allKeywords[cat].push(...kws)
                  })
                })
                const hasKeywords = Object.values(allKeywords).flat().length > 0

                return (
                  <ListItem
                    key={item.id}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    <Typography variant="caption">{item.rowNumber}.</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <RowThumbnailButton
                        bomItemPointer={pointer}
                        size="small"
                        disableClick
                      />
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body1" fontWeight="bold">
                          {item.name || item.id}
                        </Typography>
                        {hasKeywords && (
                          <Typography variant="caption" color="text.secondary">
                            {Object.values(allKeywords)
                              .flat()
                              .map((k) => k.originalKeyword)
                              .join(' | ')}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </ListItem>
                )
              })}
            </List>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common:cancel', 'Cancel')}</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || selectedActivityIds.length === 0}
        >
          {saving
            ? t('common:saving', 'Saving...')
            : t(
                'project:copy-keywords-save-count',
                'Copy keywords to {{count}} items',
                {
                  count: destinationItems.length,
                },
              )}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
